import { CompaniesMetadataSchema, CompanyType } from '@common/config/api/client'
import { StudyCompanySite } from '@features/study_details/sites/hooks'

export const SITE_COMPANY_TYPE: CompanyType = 'SITE'

export type CompanyOption = {
  label: string
  value: string
  type: CompanyType
}

/**
 * Site companies are selectable only once they have been added to the study with a Site ID, and are
 * labelled with that Site ID so that sites belonging to the same company stay distinguishable.
 *
 * `studyCompanySites` being undefined means the study sites could not be resolved (still loading, or
 * the user may not read them), in which case site companies are left unfiltered rather than hidden.
 *
 * Site options are ordered by their Site ID using a natural comparison so that numeric, alpha and
 * alphanumeric ids all sort as expected (e.g. 2 before 10, a01 before a02 before a10). Companies of
 * other types keep their original order.
 */
export const buildCompanyOptions = (
  companiesMetadata: CompaniesMetadataSchema | undefined,
  studyCompanySites: StudyCompanySite[] | undefined,
  selectedCompanyType: CompanyType | null,
): CompanyOption[] => {
  const siteByCompanyId = studyCompanySites && new Map(studyCompanySites.map((site) => [site.company_id, site]))

  type CompanyOptionEntry = { option: CompanyOption; siteId: string | null }

  const entries = (companiesMetadata?.companies ?? [])
    .filter((company) => !selectedCompanyType || company.company_type === selectedCompanyType)
    .flatMap<CompanyOptionEntry>((company) => {
      const option = { value: company.company_id, type: company.company_type }

      if (company.company_type !== SITE_COMPANY_TYPE || !siteByCompanyId) {
        return [{ option: { ...option, label: company.company_name }, siteId: null }]
      }

      const studySite = siteByCompanyId.get(company.company_id)
      return studySite
        ? [
            {
              option: { ...option, label: `${studySite.pseudo_id} - ${company.company_name}` },
              siteId: studySite.pseudo_id,
            },
          ]
        : []
    })

  const sortedSiteOptions = entries
    .filter((entry) => entry.siteId !== null)
    .sort((a, b) => a.siteId!.localeCompare(b.siteId!, undefined, { numeric: true, sensitivity: 'base' }))
    .map((entry) => entry.option)

  let siteIndex = 0
  return entries.map((entry) => (entry.siteId !== null ? sortedSiteOptions[siteIndex++] : entry.option))
}
